<template>
  <div class="pinned-comment-wrapper" role="button" tabindex="0" @click.capture="onWrapperClick">
    <div class="body">
      <div class="content-wrapper">
        <div v-if="isCodeBlock" class="block-label text-text-soft bg-input">
            {{ $t('task.Code_block') }}
        </div>
        <div v-else-if="isList" class="block-label text-text-soft bg-input">
            {{ $t('task.List') }}
        </div>
        <div v-else-if="hasVideoAttachment && !hasTextContent" class="block-label text-text-soft bg-input">
            {{ $t('task.Video') }}
        </div>
        <EditorInput
          v-else-if="hasTextContent"
          :id="cid"
          class="text pinned"
          :dontSubmitOnEnter="mobileMode"
          :reversedTag="true"
          typeOfEditor="comment"
          :allowSubmit="false"
          :key="'pinned'"
          :value="commentValue"
          :render="comment.textHTML"
          :allowEdit="false"
          :tagList="tagList"
          :needAdjustHeight="false"
          :submitOnFocusOut="false"
          :isHideFormat="true"
        />

        <div
          v-if="attachmentImages.length || attachmentFiles.length"
          class="attachments"
        >
          <div v-if="attachmentImages.length" class="images" data-image-gallery>
            <div
              v-for="attachment in attachmentImages"
              :key="attachment.id"
              class="image pinned-image"
            >
              <Picture
                maxHeight="32px"
                :maxWidth="`32px`"
                :className="getImageSize(attachment)"
                zoomable
                border
                :showTools="false"
                :uploaded="attachment.uploaded"
                :url="getAttachmentUrl(attachment)"
                :thumbHash="attachment.thumbHash || ''"
                :basePicture="attachment.basePicture || null"
                :color="attachment.dominantColor || ''"
                :imageWidth="attachment.width.toString()"
                :imageHeight="attachment.height.toString()"
                :attachment="attachment"
                :task="task"
              ></Picture>
            </div>
          </div>

          <div v-if="attachmentFiles.length" class="files">
            <template v-for="attachment in attachmentFiles" :key="attachment.id">
              <a
                class="file attachment-file"
                :class="!attachment.uploaded && 'attachment-status-info'"
                @click.prevent="downloadAttachment(attachment)"
              >
                <div class="status-wrapper">
                  <Loader v-if="(attachment.send && !attachment.uploaded)" />
                  <div class="upload-false" v-if="(!attachment.send && !attachment.uploaded)">
                    <span></span>
                  </div>
                </div>
                <PreviewFileGenerator
                  type="preview"
                  :filename="`${attachment.filename}.${attachment.extension}`"
                  :size="fileSize(attachment)"
                  extendedInfo
                  :extension="attachment.extension.toUpperCase()"
                  :attachmentId="attachment.id"
                />
              </a>
            </template>
          </div>

          <FilePreviewerPopup
            v-if="previewAttachment"
            :filename="`${previewAttachment.filename}.${previewAttachment.extension}`"
            :url="getAttachmentUrl(previewAttachment, true)"
            :user="commentator"
            :when="comment.createdAt"
            @close="previewAttachment = null"
          >
            <PdfViewer
              v-if="previewAttachment.extension.toLowerCase() === 'pdf'"
              :pdfUrl="getAttachmentUrl(previewAttachment, true)"
            />
          </FilePreviewerPopup>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import humanize from 'humanize';
import axios from 'axios';

import EditorInput from '@/components/elements/EditorInput';
import Picture from '@/components/elements/Picture';
import PreviewFileGenerator from '@/components/elements/PreviewFileGenerator';
import PdfViewer from '@/components/widgets/PdfViewer.vue';
import FilePreviewerPopup from '@/components/widgets/FilePreviewerPopup.vue';
import Loader from '@/components/elements/Loader';

const emit = defineEmits(['open']);

const { proxy } = getCurrentInstance();
const store = useStore();

const previewAttachment = ref(null);
const cid = ref(null);
const imageExtensions = ['png', 'jpeg', 'jpg', 'gif', 'bmp', 'webp'];

const props = defineProps({
  comment: Object,
  task: Object,
  user: Object,
  tagList: {
    type: Array,
    default: () => [],
  },
});

const mobileMode = computed(() => store.state.system.mobileMode);

const isCodeBlock = computed(() => {
  if (props.comment?.textMD?.includes('```')) return true;
  if (props.comment?.textDelta) {
    try {
      const delta = JSON.parse(props.comment.textDelta);
      const ops = delta.ops || delta;
      if (Array.isArray(ops)) {
        return ops.some(op => op.attributes && op.attributes['code-block']);
      }
    } catch (e) {
      console.error('PinnedComment: failed to parse textDelta');
    }
  }
  return false;
});

const isList = computed(() => {
  const md = props.comment?.textMD || '';
  if (md.includes('* ') || md.includes('- ') || md.match(/^\d+\. /m)) return true;
  if (props.comment?.textDelta) {
    try {
      const delta = JSON.parse(props.comment.textDelta);
      const ops = delta.ops || delta;
      if (Array.isArray(ops)) {
        return ops.some(op => op.attributes && op.attributes.list);
      }
    } catch (e) {
      console.error('PinnedComment: failed to parse textDelta for isList');
    }
  }
  return false;
});

const hasTextContent = computed(() => {
  const md = (props.comment?.textMD || '').trim();
  if (md) return true;

  const html = (props.comment?.textHTML || '')
    .replace(/&nbsp;/g, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return Boolean(html);
});

const commentValue = computed(() => {
  if (!props.comment) return '';
  if (!props.comment.textDelta) return props.comment.textMD;
  try {
    return JSON.parse(props.comment.textDelta);
  } catch (e) {
    console.error('PinnedComment: failed to parse textDelta');
    return props.comment.textMD;
  }
});

const commentator = computed(() => {
  return store.getters['user/getUser'](props.comment.userId);
});

const VIDEO_EXTENSIONS = ['webm', 'mkv', 'mp4', 'mov'];

function isVideoFile(attachment) {
  const ext = (attachment.extension || '').toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext);
}

function onWrapperClick() {
  const commentId = props.comment?.id;
  if (!commentId) return;
  emit('open', commentId);
}

function getImageSize(attachment, maxSize = 32) {
  const { width, height } = attachment;
  return (width < maxSize && height < maxSize) ? '' : 'as-cover';
}

function getAttachmentUrl(attachment, requireUploaded = false) {
  if (requireUploaded && !attachment.uploaded) return '';
  return `${proxy.s3base}/attachments/${attachment.workspaceId}/${attachment.projectId}/`
    + `${attachment.taskId}/${attachment.id}.${attachment.extension}`;
}

function fileSize(attachment) {
  return humanize.filesize(attachment.size, 1024, 0)
    .replace('bytes', 'B')
    .replace(' ', '');
}

const attachmentImages = computed(() => {
  const list = props.task?.attachments || [];
  const commentId = props.comment?.id;
  if (!commentId) return [];
  const extLower = (ext) => (ext || '').toLowerCase();
  return list
    .filter(a => a.commentId === commentId && imageExtensions.includes(extLower(a.extension)))
    .sort((a, b) => {
      if (typeof a.order === 'number' && typeof b.order === 'number') {
        return a.order - b.order;
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
});

const attachmentFiles = computed(() => {
  const list = props.task?.attachments || [];
  const commentId = props.comment?.id;
  if (!commentId) return [];
  const extLower = (ext) => (ext || '').toLowerCase();
  return list.filter(a => 
    a.commentId === commentId && !imageExtensions.includes(extLower(a.extension))
  );
});

const hasVideoAttachment = computed(() => {
  return attachmentFiles.value.some(a => isVideoFile(a));
});

async function downloadAttachment(attachment) {
  const ext = attachment.extension?.toLowerCase();
  if (ext === 'pdf') {
    previewAttachment.value = attachment;
    return;
  }

  const url = getAttachmentUrl(attachment, true);
  if (!url) return;

  const filename = `${attachment.filename}.${attachment.extension}`;
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('PinnedComment: failed to download attachment', { 
      attachmentId: attachment?.id, 
      filename, 
      error 
    });
  }
}

onMounted(() => {
  cid.value = window.utils.genId();
});
</script>

<style lang="scss" scoped>
.pinned-comment-wrapper {
  display: flex;
  width: 100%;
  cursor: pointer;
}

.body {
  width: 100%;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
}

/* Pinned comment preview: fixed height with ellipsis when content overflows */
.pinned-comment-wrapper :deep(.multilineinput-wrapper.text.pinned .title) {
  box-sizing: border-box;
  height: 32px;
  max-height: 32px;
  overflow: hidden !important;
  line-height: 16px;
  display: -webkit-box !important;
  -webkit-box-orient: vertical !important;
  line-clamp: 2 !important;
  -webkit-line-clamp: 2 !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  text-overflow: ellipsis;
  padding: 0 !important;
  border: none !important;
}

.pinned-comment-wrapper :deep(.multilineinput-wrapper.text.pinned .title *) {
  display: inline;
  margin: 0;
  padding: 0;
}

.pinned-comment-wrapper :deep(.multilineinput-wrapper.text.pinned .title p + p)::before {
  content: ' ';
}

.block-label {
  height: 32px;
  line-height: 32px;
  font-size: var(--fs-body);
  padding: 0px 8px;
  font-style: italic;
  border-radius: 4px;
  width: fit-content;
}

.pinned-comment-wrapper :deep(.multilineinput-wrapper.text.pinned .title br) {
  display: none;
}

  .pinned-image {
    min-height: 0;
    width: 32px;
    max-width: 32px;
    max-height: 32px;
    margin: 0;
  }
</style>
